import { ReportWidgetAccentColor } from 'src/api/generated';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';

/**
 * Frontend presentation metadata for one widget accent color option.
 */
export interface ReportWidgetAccentColorOption {
  /** Persisted semantic color value. */
  value: ReportWidgetAccentColor;
  /** Translation key shown in the editor palette. */
  labelKey: TranslationKey;
  /** Swatch fill color used in the editor palette. */
  swatchClassName: string;
  /** Subtle surface classes used to tint the widget card. */
  surfaceClassName: string;
  /** Header classes used to tint the widget card header. */
  headerClassName: string;
  /** Badge classes used when the label is rendered on the widget card. */
  badgeClassName: string;
  /** Selection ring classes used in the editor palette. */
  selectedClassName: string;
}

const REPORT_WIDGET_ACCENT_COLOR_DEFINITIONS: Record<
  ReportWidgetAccentColor,
  ReportWidgetAccentColorOption
> = {
  [ReportWidgetAccentColor.PRIMARY]: {
    value: ReportWidgetAccentColor.PRIMARY,
    labelKey: 'reports.dashboard.color.primary',
    swatchClassName: 'bg-primary-500',
    surfaceClassName: 'border-primary-200 bg-primary-50',
    headerClassName:
      'border-b border-primary-200 bg-gradient-to-r from-primary-100 via-primary-50 to-white',
    badgeClassName: 'border-primary-500 bg-primary-500 text-white',
    selectedClassName: 'border-primary-300 ring-primary-100 bg-primary-50',
  },
  [ReportWidgetAccentColor.SECONDARY]: {
    value: ReportWidgetAccentColor.SECONDARY,
    labelKey: 'reports.dashboard.color.secondary',
    swatchClassName: 'bg-secondary-500',
    surfaceClassName: 'border-secondary-200 bg-secondary-50',
    headerClassName:
      'border-b border-secondary-200 bg-gradient-to-r from-secondary-100 via-secondary-50 to-white',
    badgeClassName: 'border-secondary-500 bg-secondary-500 text-white',
    selectedClassName:
      'border-secondary-300 ring-secondary-100 bg-secondary-50',
  },
  [ReportWidgetAccentColor.NEUTRAL]: {
    value: ReportWidgetAccentColor.NEUTRAL,
    labelKey: 'reports.dashboard.color.neutral',
    swatchClassName: 'bg-neutral-500',
    surfaceClassName: 'border-neutral-200 bg-white',
    headerClassName:
      'border-b border-neutral-100 bg-gradient-to-r from-white to-neutral-50',
    badgeClassName: 'border-neutral-500 bg-neutral-500 text-white',
    selectedClassName: 'border-neutral-300 ring-neutral-100 bg-neutral-50',
  },
  [ReportWidgetAccentColor.DANGER]: {
    value: ReportWidgetAccentColor.DANGER,
    labelKey: 'reports.dashboard.color.danger',
    swatchClassName: 'bg-danger-500',
    surfaceClassName: 'border-danger-200 bg-danger-50',
    headerClassName:
      'border-b border-danger-200 bg-gradient-to-r from-danger-100 via-danger-50 to-white',
    badgeClassName: 'border-danger-500 bg-danger-500 text-white',
    selectedClassName: 'border-danger-300 ring-danger-100 bg-danger-50',
  },
  [ReportWidgetAccentColor.WARNING]: {
    value: ReportWidgetAccentColor.WARNING,
    labelKey: 'reports.dashboard.color.warning',
    swatchClassName: 'bg-warning-400',
    surfaceClassName: 'border-warning-200 bg-warning-50',
    headerClassName:
      'border-b border-warning-200 bg-gradient-to-r from-warning-100 via-warning-50 to-white',
    badgeClassName: 'border-warning-400 bg-warning-400 text-primary-900',
    selectedClassName: 'border-warning-300 ring-warning-100 bg-warning-50',
  },
  [ReportWidgetAccentColor.SUCCESS]: {
    value: ReportWidgetAccentColor.SUCCESS,
    labelKey: 'reports.dashboard.color.success',
    swatchClassName: 'bg-success-400',
    surfaceClassName: 'border-success-200 bg-success-50',
    headerClassName:
      'border-b border-success-200 bg-gradient-to-r from-success-100 via-success-50 to-white',
    badgeClassName: 'border-success-500 bg-success-500 text-white',
    selectedClassName: 'border-success-300 ring-success-100 bg-success-50',
  },
  [ReportWidgetAccentColor.INFO]: {
    value: ReportWidgetAccentColor.INFO,
    labelKey: 'reports.dashboard.color.info',
    swatchClassName: 'bg-info-500',
    surfaceClassName: 'border-info-200 bg-info-50',
    headerClassName:
      'border-b border-info-200 bg-gradient-to-r from-info-100 via-info-50 to-white',
    badgeClassName: 'border-info-500 bg-info-500 text-white',
    selectedClassName: 'border-info-300 ring-info-100 bg-info-50',
  },
  [ReportWidgetAccentColor.WHATSAPP]: {
    value: ReportWidgetAccentColor.WHATSAPP,
    labelKey: 'reports.dashboard.color.whatsapp',
    swatchClassName: 'bg-whatsapp-500',
    surfaceClassName: 'border-whatsapp-200 bg-whatsapp-50',
    headerClassName:
      'border-b border-whatsapp-200 bg-gradient-to-r from-whatsapp-100 via-whatsapp-50 to-white',
    badgeClassName: 'border-whatsapp-500 bg-whatsapp-500 text-white',
    selectedClassName: 'border-whatsapp-300 ring-whatsapp-100 bg-whatsapp-50',
  },
  [ReportWidgetAccentColor.CALL]: {
    value: ReportWidgetAccentColor.CALL,
    labelKey: 'reports.dashboard.color.call',
    swatchClassName: 'bg-call-500',
    surfaceClassName: 'border-call-200 bg-call-50',
    headerClassName:
      'border-b border-call-200 bg-gradient-to-r from-call-100 via-call-50 to-white',
    badgeClassName: 'border-call-500 bg-call-500 text-white',
    selectedClassName: 'border-call-300 ring-call-100 bg-call-50',
  },
  [ReportWidgetAccentColor.EMAIL]: {
    value: ReportWidgetAccentColor.EMAIL,
    labelKey: 'reports.dashboard.color.email',
    swatchClassName: 'bg-email-500',
    surfaceClassName: 'border-email-200 bg-email-50',
    headerClassName:
      'border-b border-email-200 bg-gradient-to-r from-email-100 via-email-50 to-white',
    badgeClassName: 'border-email-500 bg-email-500 text-white',
    selectedClassName: 'border-email-300 ring-email-100 bg-email-50',
  },
};

/**
 * Default accent color used for widget labels when one was not chosen explicitly.
 */
export const DEFAULT_REPORT_WIDGET_ACCENT_COLOR =
  ReportWidgetAccentColor.NEUTRAL;

/**
 * Ordered list of label-color options shown in the widget editor.
 */
export const REPORT_WIDGET_ACCENT_COLOR_OPTIONS: ReportWidgetAccentColorOption[] =
  [
    REPORT_WIDGET_ACCENT_COLOR_DEFINITIONS[ReportWidgetAccentColor.NEUTRAL],
    REPORT_WIDGET_ACCENT_COLOR_DEFINITIONS[ReportWidgetAccentColor.PRIMARY],
    REPORT_WIDGET_ACCENT_COLOR_DEFINITIONS[ReportWidgetAccentColor.SECONDARY],
    REPORT_WIDGET_ACCENT_COLOR_DEFINITIONS[ReportWidgetAccentColor.INFO],
    REPORT_WIDGET_ACCENT_COLOR_DEFINITIONS[ReportWidgetAccentColor.SUCCESS],
    REPORT_WIDGET_ACCENT_COLOR_DEFINITIONS[ReportWidgetAccentColor.WARNING],
    REPORT_WIDGET_ACCENT_COLOR_DEFINITIONS[ReportWidgetAccentColor.DANGER],
  ];

/**
 * Resolves one accent color option, falling back to the default dashboard color.
 */
export const getReportWidgetAccentColorOption = (
  color?: ReportWidgetAccentColor | null
): ReportWidgetAccentColorOption =>
  REPORT_WIDGET_ACCENT_COLOR_DEFINITIONS[
    color ?? DEFAULT_REPORT_WIDGET_ACCENT_COLOR
  ];
